/**
 * Save plays — the inversion of the Customer Success cockpit.
 *
 * The old screen was an analysis-to-read; this module turns it into a
 * plan-to-approve: `rankSaveList` answers "Who do I save today?" with at
 * most five accounts ranked by (churn probability × ARR at stake), each
 * carrying ONE recommended play chosen deterministically from the churn
 * engine's own top contributor:
 *
 *   past-due billing (marginDrift)          → payment-reminder
 *   quiet Nd (engagement/champion/usage/…)  → re-engagement check-in
 *   blocked deliverable (deliverableSlippage) → unblock-update
 *   renewal near (dealStall)                → renewal-prep
 *
 * `composeSavePlay` drafts the play's email STRICTLY from real record
 * fields (amounts, dates, day counts, names) and returns null when the
 * essentials are missing — no filler, no invented numbers, ever.
 *
 * Deliberately PURE: no store, no network, no clock unless the caller
 * omits `now`. The churn math is REUSED (`explainDrivers` picks the top
 * contributor with the engine's own canonical tie-break) — never
 * re-implemented. Sends stay approval-gated: composing here produces a
 * draft only; the ONLY send path remains the `send_customer_update`
 * proposal → approvals inbox → executor.
 */

import type { Deliverable, Person } from '../db/types';
import type { CustomerHealth } from '../crm/health';
import type { ScanFinding } from '../agent/systemScanner';
import {
  explainDrivers,
  type ChurnRiskResult,
  type RiskDimensionKey,
} from '../crm/churnRisk';
import { isDeliverableOverdue } from '../crm/deliverablesSummary';
import { lastTouchAt } from '../crm/score';
import { escapeHtml } from '../crm/weeklyUpdate';
import {
  resolveCustomerUpdateRecipient,
  type CustomerUpdateRecipient,
} from './weeklyUpdate';

const DAY_MS = 86_400_000;

// ===========================================================================
// Plays
// ===========================================================================

export type SavePlayKind =
  | 'payment-reminder'
  | 're-engagement'
  | 'unblock-update'
  | 'renewal-prep';

/** Human labels for the one recommended play on each card. */
export const SAVE_PLAY_LABELS: Record<SavePlayKind, string> = {
  'payment-reminder': 'Payment reminder',
  're-engagement': 'Re-engagement check-in',
  'unblock-update': 'Unblock update',
  'renewal-prep': 'Renewal prep',
};

/**
 * Deterministic contributor → play mapping. Every churn dimension resolves
 * to exactly one play so the recommendation never wobbles between renders:
 *   - marginDrift is past-due billing → payment reminder;
 *   - deliverableSlippage is blocked/overdue commitments → unblock update;
 *   - dealStall is renewal proximity → renewal prep;
 *   - the silence-shaped dimensions (engagement, champion, usage, ticket
 *     churn) all mean "talk to them" → re-engagement check-in.
 */
export const PLAY_BY_DIMENSION: Record<RiskDimensionKey, SavePlayKind> = {
  marginDrift: 'payment-reminder',
  deliverableSlippage: 'unblock-update',
  dealStall: 'renewal-prep',
  engagementRecency: 're-engagement',
  championActivity: 're-engagement',
  usage: 're-engagement',
  ticketVelocity: 're-engagement',
};

/** The play for a churn dimension — exported for direct testing. */
export function playForDimension(key: RiskDimensionKey): SavePlayKind {
  return PLAY_BY_DIMENSION[key];
}

// ===========================================================================
// rankSaveList — the ranked plan
// ===========================================================================

/** Hard cap: the operator saves at most this many accounts in one day. */
export const SAVE_LIST_CAP = 5;

/**
 * Churn probability (0–100) at/above which an account "needs saving".
 * Reuses the shipped med-tier boundary (`crm/healthExplain.churnTier`:
 * probability ≥ 0.25 → 'med') — no new risk math is invented here.
 */
export const SAVE_FLOOR_PROBABILITY = 25;

/** One already-scored customer, as the view holds it (never re-scored). */
export interface SaveCandidate {
  person: Person;
  /** The existing health score (crm/health) — reused for the dial. */
  health: CustomerHealth;
}

/** One card on the Save-list: account + the ONE recommended play. */
export interface SaveListEntry {
  person: Person;
  health: CustomerHealth;
  churn: ChurnRiskResult;
  /** The one deterministic play, from the top contributor. */
  play: SavePlayKind;
  playLabel: string;
  /** The churn dimension that chose the play. */
  topDimension: RiskDimensionKey;
  /** One-line why — the churn engine's own top-driver narrative. */
  why: string;
  /** Real ARR on record; 0 when none is set (see `hasArr`). */
  arrAtStake: number;
  /** False when the account carries no value — render honestly, never $0. */
  hasArr: boolean;
  /** Ranking key: churnProbability/100 × ARR. */
  stake: number;
  /** ~days before the risk becomes irreversible (churn engine's number). */
  runwayDays: number;
  /** System-scan findings tied to THIS record — the Why? fold's evidence. */
  findings: ScanFinding[];
  /** Resolved send-to; null renders the honest "needs email" state. */
  recipient: CustomerUpdateRecipient | null;
}

/**
 * Rank the book into the Save-list: at most `SAVE_LIST_CAP` accounts that
 * genuinely need saving, ordered by (churn probability × ARR at stake).
 * Deterministic ties: higher churn probability first, then name A→Z.
 *
 * Honest exclusions — an account never makes the list when:
 *   - it has no measured churn result (no signal → no fabricated risk);
 *   - its probability sits below the med-tier floor (stable);
 *   - no dimension contributes positively (nothing to act on).
 */
export function rankSaveList(
  customers: readonly SaveCandidate[],
  churn: readonly ChurnRiskResult[],
  findings: readonly ScanFinding[] = [],
): SaveListEntry[] {
  const churnById = new Map(churn.map((r) => [r.accountId, r]));

  const entries: SaveListEntry[] = [];
  for (const { person, health } of customers) {
    const risk = churnById.get(person.id);
    if (!risk) continue; // no measured signal — never invent one
    if (risk.churnProbability < SAVE_FLOOR_PROBABILITY) continue; // stable

    const topDriver = explainDrivers(risk)[0];
    if (!topDriver || topDriver.contribution <= 0) continue; // nothing to act on

    const arr = risk.accountValue ?? person.accountValue;
    const arrAtStake = typeof arr === 'number' ? arr : 0;
    const play = playForDimension(topDriver.key);

    entries.push({
      person,
      health,
      churn: risk,
      play,
      playLabel: SAVE_PLAY_LABELS[play],
      topDimension: topDriver.key,
      why: topDriver.text,
      arrAtStake,
      hasArr: typeof arr === 'number',
      stake: (risk.churnProbability / 100) * arrAtStake,
      runwayDays: risk.leadTimeDays,
      findings: findings.filter((f) => f.entityId === person.id),
      recipient: resolveCustomerUpdateRecipient(person),
    });
  }

  return entries
    .sort(
      (a, b) =>
        b.stake - a.stake ||
        b.churn.churnProbability - a.churn.churnProbability ||
        a.person.name.localeCompare(b.person.name),
    )
    .slice(0, SAVE_LIST_CAP);
}

// ===========================================================================
// composeSavePlay — the drafted artifact
// ===========================================================================

/** The live boards a play may need. All optional — absent means empty. */
export interface SavePlayData {
  /** First-class deliverables (matched to the customer's company). */
  deliverables?: Deliverable[];
  /** Injected clock for determinism. */
  now?: number;
}

/** The rendered save-play email. `text` always present; `html` Gmail-safe. */
export interface ComposedSavePlay {
  subject: string;
  text: string;
  html: string;
}

/** Minimal inline-styled HTML from plain text — reused after inline edits. */
export function savePlayHtml(text: string): string {
  const body = text.split('\n').map(escapeHtml).join('<br/>');
  return (
    `<div style="font-family:-apple-system,'Segoe UI',Helvetica,Arial,sans-serif;` +
    `max-width:600px;margin:0 auto;padding:24px;color:#1a1a1a;background:#ffffff;` +
    `line-height:1.5;">${body}</div>`
  );
}

function firstNameOf(customer: Person): string {
  const recipient = resolveCustomerUpdateRecipient(customer);
  return (recipient?.name ?? customer.name).trim().split(/\s+/)[0];
}

function wrapEmail(
  customer: Person,
  subject: string,
  bodyLines: string[],
  ourCompany: string,
): ComposedSavePlay {
  const lines = [
    `Hi ${firstNameOf(customer)},`,
    '',
    ...bodyLines,
    '',
    'Anything look off? Just reply to this email.',
    `— ${ourCompany}`,
  ];
  const text = lines.join('\n');
  return { subject, text, html: savePlayHtml(text) };
}

function usd(n: number): string {
  return `$${n.toLocaleString('en-US')}`;
}

/**
 * Compose the drafted email for one save play, STRICTLY from real record
 * fields. Returns null when the play's essentials are missing from the
 * record — a payment reminder with no past-due invoice, a re-engagement
 * note with no real quiet gap, an unblock update with nothing blocked, a
 * renewal prep with no renewal date. Null means "no draft", never filler.
 */
export function composeSavePlay(
  customer: Person,
  play: SavePlayKind,
  data: SavePlayData = {},
  ourCompany = 'Argo',
): ComposedSavePlay | null {
  const now = data.now ?? Date.now();

  switch (play) {
    case 'payment-reminder': {
      const overdue = (customer.billingDates ?? []).filter((bd) => {
        const t = Date.parse(bd.date);
        return Number.isFinite(t) && t < now && (bd.status ?? 'scheduled') !== 'paid';
      });
      if (overdue.length === 0) return null; // nothing past due — no reminder
      const subject =
        overdue.length === 1
          ? 'Quick reminder: an invoice is past due'
          : `Quick reminder: ${overdue.length} invoices are past due`;
      return wrapEmail(
        customer,
        subject,
        [
          'A quick note on billing — our records show the following past due:',
          ...overdue.map(
            (bd) =>
              `  - ${bd.label} (due ${bd.date}${bd.amount != null ? ` · ${usd(bd.amount)}` : ''})`,
          ),
          '',
          'If payment is already on its way, please disregard — otherwise happy to help sort it out.',
        ],
        ourCompany,
      );
    }

    case 're-engagement': {
      const last = lastTouchAt(customer);
      const t = last !== undefined ? Date.parse(last) : NaN;
      if (!Number.isFinite(t)) return null; // no real touch on record
      const quietDays = Math.floor((now - t) / DAY_MS);
      if (quietDays < 1) return null; // touched today — no honest quiet gap
      return wrapEmail(
        customer,
        `Checking in — it's been ${quietDays} day${quietDays === 1 ? '' : 's'}`,
        [
          `It's been ${quietDays} day${quietDays === 1 ? '' : 's'} since we last connected, and we want to make sure you're getting everything you need from us.`,
          '',
          'Do you have 20 minutes this week for a quick check-in?',
        ],
        ourCompany,
      );
    }

    case 'unblock-update': {
      const company = (customer.company ?? '').trim().toLowerCase();
      const blocked: { title: string; dueDate?: string; state: string }[] = [
        ...(customer.deliverables ?? [])
          .filter((d) => d.status === 'blocked')
          .map((d) => ({
            title: d.title,
            ...(d.dueDate ? { dueDate: d.dueDate } : {}),
            state: 'blocked',
          })),
        ...(data.deliverables ?? [])
          .filter(
            (d) =>
              company !== '' &&
              d.accountName.trim().toLowerCase() === company &&
              isDeliverableOverdue(d, now),
          )
          .map((d) => ({
            title: d.name,
            ...(d.dueDate ? { dueDate: d.dueDate } : {}),
            state: 'overdue',
          })),
      ];
      if (blocked.length === 0) return null; // nothing blocked — no update
      const subject =
        blocked.length === 1
          ? `Where things stand on “${blocked[0].title}”`
          : `Where things stand on ${blocked.length} deliverables`;
      return wrapEmail(
        customer,
        subject,
        [
          'We owe you a straight update on work that has slipped:',
          ...blocked.map(
            (d) => `  - ${d.title}${d.dueDate ? ` (due ${d.dueDate})` : ''} — ${d.state}`,
          ),
          '',
          'Can we grab 15 minutes to walk through what unblocks each of these?',
        ],
        ourCompany,
      );
    }

    case 'renewal-prep': {
      const t = customer.renewalDate ? Date.parse(customer.renewalDate) : NaN;
      if (!Number.isFinite(t)) return null; // no renewal date on record
      const days = Math.round((t - now) / DAY_MS);
      const dateLabel = customer.renewalDate as string;
      const subject =
        days >= 0
          ? `Planning ahead: your renewal on ${dateLabel}`
          : `Your renewal date (${dateLabel}) has passed — let's talk`;
      const firstLine =
        days >= 0
          ? `Your contract renews on ${dateLabel} — ${days} day${days === 1 ? '' : 's'} out.`
          : `Your contract renewal date (${dateLabel}) passed ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago.`;
      return wrapEmail(
        customer,
        subject,
        [
          firstLine,
          '',
          "We'd love to set up a short call to review where things stand and plan the next term. What does next week look like?",
        ],
        ourCompany,
      );
    }
  }
}
